import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CircularProgress,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  formatInstallationModel,
  getDeviceTypeName
} from '../Information/installationSetupUtils';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  CHECKLIST_ENABLED_PRODUCTS,
  ChecklistSummary
} from 'src/interfaces/ChecklistTypes';
import {
  BatteryAnomalySummary,
  VOLTAGE_ANOMALY_PRODUCTS
} from 'src/interfaces/BatteryAnomalyTypes';
import SetupProgress from '../Checklist/SetupProgress';
import { OnSiteChecklistSummary } from 'src/interfaces/OnSiteChecklistTypes';
import useIsMobile from '../../../hooks/useIsMobile';

interface FlatInstallationViewProps {
  installations: I_Installation[];
  product?: number;
}

const FlatInstallationView = (props: FlatInstallationViewProps) => {
  // Phones (<md): stacked cards instead of the wide table. Desktop keeps the table.
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const intl = useIntl();
  const [selectedInstallation, setSelectedInstallation] = useState<number>(-1);
  const currentLocation = useLocation();
  const baseRoute = props.product === 5 ? routes.sodistorepro_installations : routes.sodiohome_installations;

  const { currentUser } = useContext(UserContext);
  const showChecklistColumn =
    currentUser?.userType === UserType.admin &&
    CHECKLIST_ENABLED_PRODUCTS.has(props.product ?? -1);
  const showAnomalyColumn =
    currentUser?.userType === UserType.admin &&
    VOLTAGE_ANOMALY_PRODUCTS.has(props.product ?? -1);
  // Installer Installation Checklist is sodistore home only (product 2).
  const showOnSiteColumn =
    currentUser?.userType === UserType.admin && (props.product ?? -1) === 2;
  const isListViewPath =
    currentLocation.pathname === baseRoute + 'list' ||
    currentLocation.pathname === baseRoute + routes.list;
  const [progressMap, setProgressMap] = useState<Map<number, ChecklistSummary>>(
    new Map()
  );
  const [anomalyMap, setAnomalyMap] = useState<Map<number, BatteryAnomalySummary>>(
    new Map()
  );
  const [onSiteProgressMap, setOnSiteProgressMap] = useState<
    Map<number, OnSiteChecklistSummary>
  >(new Map());

  useEffect(() => {
    if (!showChecklistColumn || !isListViewPath) return;
    axiosConfig
      .get('/GetChecklistSummary')
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const map = new Map<number, ChecklistSummary>();
        res.data.forEach((s: ChecklistSummary) => {
          map.set(s.installationId, s);
        });
        setProgressMap(map);
      })
      .catch(() => setProgressMap(new Map()));
  }, [showChecklistColumn, isListViewPath]);

  useEffect(() => {
    if (!showAnomalyColumn || !isListViewPath) return;
    axiosConfig
      .get('/GetBatteryVoltageAnomalySummary')
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const map = new Map<number, BatteryAnomalySummary>();
        res.data.forEach((s: BatteryAnomalySummary) => {
          map.set(s.installationId, s);
        });
        setAnomalyMap(map);
      })
      .catch(() => setAnomalyMap(new Map()));
  }, [showAnomalyColumn, isListViewPath]);

  useEffect(() => {
    if (!showOnSiteColumn || !isListViewPath) return;
    axiosConfig
      .get('/GetOnSiteChecklistSummary')
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const map = new Map<number, OnSiteChecklistSummary>();
        res.data.forEach((s: OnSiteChecklistSummary) => {
          map.set(s.installationId, s);
        });
        setOnSiteProgressMap(map);
      })
      .catch(() => setOnSiteProgressMap(new Map()));
  }, [showOnSiteColumn, isListViewPath]);

  const sortedInstallations = useMemo(() => {
    return [...props.installations].sort((a, b) => {
      // Data-collection-disabled installations sink below everything (even offline).
      const aDisabled = a.dataCollectionEnabled === false;
      const bDisabled = b.dataCollectionEnabled === false;
      if (aDisabled !== bDisabled) return aDisabled ? 1 : -1;

      // Then sort by status (alarms first)
      const a_status = a.status;
      const b_status = b.status;

      if (a_status > b_status) return -1;
      if (a_status < b_status) return 1;

      // Finally by installation SN, smallest first. SNs are free-text strings,
      // so compare numerically where they look numeric; installations without
      // an SN sink to the bottom of their group.
      const a_sn = (a.serialNumber ?? '').trim();
      const b_sn = (b.serialNumber ?? '').trim();
      if (!a_sn || !b_sn) return a_sn ? -1 : b_sn ? 1 : 0;

      return a_sn.localeCompare(b_sn, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }, [props.installations]);

  const handleSelectOneInstallation = (installationID: number): void => {
    if (selectedInstallation != installationID) {
      setSelectedInstallation(installationID);
      setSelectedInstallation(-1);

      const target = props.installations.find((i) => i.id === installationID);
      const landingTab =
        target?.dataCollectionEnabled === false ? routes.information : routes.live;

      navigate(
        baseRoute +
          routes.list +
          routes.installation +
          `${installationID}` +
          '/' +
          landingTab,
        {
          replace: true
        }
      );
    } else {
      setSelectedInstallation(-1);
    }
  };

  // The anomaly warning icon jumps straight to the Battery View tab, which is
  // where the full-charge low-voltage log lives.
  const handleOpenBatteryView = (
    event: React.MouseEvent<Element>,
    installationID: number
  ): void => {
    // Without this the row's own click also fires and lands on the Live tab.
    event.stopPropagation();

    navigate(
      baseRoute +
        routes.list +
        routes.installation +
        `${installationID}` +
        '/' +
        routes.batteryview,
      {
        replace: true
      }
    );
  };

  const theme = useTheme();

  const HoverableTableRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover // Or any other color
    }
  }));

  const isListView = isListViewPath;

  return (
    <Grid container spacing={1} sx={{ marginTop: 0.1 }}>
      <Grid
        item
        xs={12}
        md="auto"
        sx={{
          display: isListView ? 'block' : 'none'
        }}
      >
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sortedInstallations.map((installation) => (
              <Card
                key={installation.id}
                onClick={() => handleSelectOneInstallation(installation.id)}
                sx={{ p: 2, cursor: 'pointer' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="text.primary"
                    sx={{ fontSize: 'small' }}
                  >
                    {installation.name}
                  </Typography>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flexShrink: 0
                    }}
                  >
                    {installation.testingMode && (
                      <BuildIcon
                        style={{ width: 18, height: 18, color: 'purple' }}
                      />
                    )}
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor:
                          installation.dataCollectionEnabled === false
                            ? 'grey'
                            : installation.status === 2
                            ? 'red'
                            : installation.status === 1
                            ? 'orange'
                            : installation.status === -1
                            ? 'red'
                            : installation.status === -2
                            ? '#f7b34d'
                            : 'green'
                      }}
                    />
                  </div>
                </div>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', textTransform: 'none' }}
                >
                  {[
                    formatInstallationModel(
                      installation.product,
                      installation.installationModel
                    ),
                    getDeviceTypeName(installation.device),
                    installation.city
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {installation.serialNumber && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textTransform: 'none' }}
                  >
                    {installation.serialNumber}
                  </Typography>
                )}
              </Card>
            ))}
          </div>
        ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="name" defaultMessage="Name" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="model" defaultMessage="Model" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="DeviceType" defaultMessage="Device Type" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="installationSN" defaultMessage="Installation SN" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="city" defaultMessage="City" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="status" defaultMessage="Status" />
                  </TableCell>
                  {showChecklistColumn && (
                    <TableCell>
                      <FormattedMessage
                        id="setupProgress"
                        defaultMessage="Monitor Onboarding Progress"
                      />
                    </TableCell>
                  )}
                  {showOnSiteColumn && (
                    <TableCell>
                      <FormattedMessage
                        id="installationChecklistProgress"
                        defaultMessage="Installation Checklist Progress"
                      />
                    </TableCell>
                  )}
                  {showAnomalyColumn && (
                    <TableCell>
                      <FormattedMessage
                        id="batteryVoltageAnomaly"
                        defaultMessage="Full @ low voltage"
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInstallations
                  // .filter(
                  //   (installation) =>
                  //     installation.status === -1 &&
                  //     installation.testingMode == false
                  // )
                  .map((installation) => {
                    const isInstallationSelected =
                      installation.id === selectedInstallation;

                    const status = installation.status;

                    return (
                      <HoverableTableRow
                        key={installation.id}
                        onClick={() =>
                          handleSelectOneInstallation(installation.id)
                        }
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {formatInstallationModel(
                              installation.product,
                              installation.installationModel
                            )}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {getDeviceTypeName(installation.device)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.serialNumber}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.city || ''}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: '15px'
                            }}
                          >
                            {installation.dataCollectionEnabled === false ? (
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  marginLeft: '2px',
                                  borderRadius: '50%',
                                  backgroundColor: 'grey'
                                }}
                              />
                            ) : (
                              <>
                                {status === -1 ? (
                                  <CancelIcon
                                    style={{
                                      width: '23px',
                                      height: '23px',
                                      color: 'red',
                                      borderRadius: '50%'
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {status === -2 ? (
                                  <CircularProgress
                                    size={20}
                                    sx={{
                                      color: '#f7b34d'
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                <div
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    marginLeft: '2px',
                                    borderRadius: '50%',
                                    backgroundColor:
                                      status === 2
                                        ? 'red'
                                        : status === 1
                                        ? 'orange'
                                        : status === -1 || status === -2
                                        ? 'transparent'
                                        : 'green'
                                  }}
                                />
                              </>
                            )}
                            {installation.testingMode && (
                              <BuildIcon
                                style={{
                                  width: '23px',
                                  height: '23px',
                                  color: 'purple',
                                  borderRadius: '50%',
                                  position: 'relative',
                                  zIndex: 1,
                                  marginLeft: status != -1 ? '25px' : '0px'
                                }}
                              />
                            )}
                          </div>
                        </TableCell>
                        {showChecklistColumn && (
                          <TableCell>
                            {(() => {
                              const summary = progressMap.get(installation.id);
                              return (
                                <SetupProgress
                                  done={summary?.done ?? 0}
                                  total={summary?.total ?? 13}
                                />
                              );
                            })()}
                          </TableCell>
                        )}
                        {showOnSiteColumn && (
                          <TableCell>
                            {(() => {
                              const summary = onSiteProgressMap.get(installation.id);
                              return (
                                <SetupProgress
                                  done={summary?.done ?? 0}
                                  total={summary?.total ?? 8}
                                />
                              );
                            })()}
                          </TableCell>
                        )}
                        {showAnomalyColumn && (
                          <TableCell>
                            {anomalyMap.has(installation.id) && (
                              <Tooltip
                                title={intl.formatMessage({
                                  id: 'openFullChargeLowVoltageLog',
                                  defaultMessage:
                                    'Open the full-charge low-voltage log'
                                })}
                              >
                                <WarningAmberIcon
                                  onClick={(event) =>
                                    handleOpenBatteryView(event, installation.id)
                                  }
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    color: '#f7b34d',
                                    cursor: 'pointer'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </HoverableTableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        )}
      </Grid>
    </Grid>
  );
};

export default FlatInstallationView;
